/**
 * RELATÓRIO DE RASTREABILIDADE DO TRAFO — a folha para imprimir ou salvar em PDF.
 *
 * Pedido da Gestão (14/10/2026): "armazenar o histórico e ter a opção de
 * imprimir ou salvar em PDF para ter rastreabilidade do processo". O histórico
 * já é gravado a cada ação (trafos_reforma_eventos); aqui ele vira um documento:
 * os dados de cada etapa como estão gravados, a passagem pelas etapas (com o
 * tempo em cada uma), o histórico completo — quem fez o quê e quando — e as
 * fotos como anexo.
 *
 * É .ts puro (sem JSX): uma janela de impressão montada como string, como o
 * laudo do DISC no RH. O "Salvar em PDF" é o do próprio navegador, na janela de
 * impressão — funciona no computador e no celular sem biblioteca nenhuma.
 *
 * As cores daqui são fixas de propósito: a janela de impressão não carrega o
 * styles.css e o papel não tem modo escuro.
 *
 * ARQUIVO IGUAL no sistemaColaborador e no sistemaOperacional.
 */
import { ApiError } from "./api";
import {
  ANALISES_OLEO,
  CAMPOS_PLACA,
  CONCLUIDO,
  ENSAIOS_ENTRADA,
  ENSAIOS_FINAIS,
  ETAPAS,
  INSPECAO_FINAL,
  apiTrafos,
  dataBR,
  descricaoTrafo,
  infoEtapa,
  nomeEtapa,
  type Ensaio,
  type EventoTrafo,
  type ItemInspecao,
  type RastreabilidadeTrafo,
} from "./trafos";

/* ------------------------------------------------------------ auxiliares --- */

function esc(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** "13/10/2026 14:32" — no fuso de quem imprime. */
function dataHoraLonga(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Duração legível: "45 min", "3 h 20 min", "2 dias 4 h". */
function duracaoTexto(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "menos de 1 min";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h${min % 60 ? ` ${min % 60} min` : ""}`;
  const dias = Math.floor(h / 24);
  return `${dias} dia${dias > 1 ? "s" : ""}${h % 24 ? ` ${h % 24} h` : ""}`;
}

/** Dias corridos entre duas datas AAAA-MM-DD (sem passar por fuso). */
function diasEntre(de: string, ate: string): number | null {
  const a = Date.parse(`${de.slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${ate.slice(0, 10)}T00:00:00Z`);
  return Number.isFinite(a) && Number.isFinite(b) ? Math.round((b - a) / 86_400_000) : null;
}

/** AAAA-MM-DD local de um instante (a conclusão é TIMESTAMPTZ). */
function ymdLocal(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const vazio = (v: unknown) => String(v ?? "").trim() === "";
const ou = (v: unknown) => (vazio(v) ? "—" : esc(v));

/** Classe de cor do resultado (verde / vermelho), para saltar aos olhos no papel. */
function tom(resultado: string): string {
  if (resultado === "Aprovado" || resultado === "Conforme") return "ok";
  if (resultado === "Reprovado" || resultado === "Não conforme") return "nok";
  return "";
}

/** Linhas na ordem do formulário (a lista padrão primeiro, depois as a mais). */
function naOrdem<T extends object>(gravadas: T[], padrao: string[], chave: keyof T, vazioDe: (nome: string) => T): T[] {
  const lista = Array.isArray(gravadas) ? gravadas : [];
  const porNome = new Map(lista.map((x) => [String(x[chave]), x]));
  const extras = lista.filter((x) => !padrao.includes(String(x[chave])));
  return [...padrao.map((n) => porNome.get(n) ?? vazioDe(n)), ...extras];
}

/* ---------------------------------------------------------------- blocos --- */

function tabelaKV(linhas: [string, string][]): string {
  return `<table class="kv">${linhas
    .map(([k, v]) => `<tr><th>${esc(k)}</th><td>${v}</td></tr>`)
    .join("")}</table>`;
}

function tabelaEnsaios(ensaios: Ensaio[], padrao: string[]): string {
  const linhas = naOrdem(ensaios, padrao, "ensaio", (n) => ({ ensaio: n, resultado: "", valores: "", obs: "" }));
  return `<table><thead><tr><th>Ensaio</th><th class="res">Resultado</th><th>Valores</th><th>Observação</th></tr></thead><tbody>${linhas
    .map(
      (e) =>
        `<tr><td>${esc(e.ensaio)}</td><td class="res ${tom(e.resultado)}">${ou(e.resultado)}</td><td>${ou(
          e.valores,
        )}</td><td>${ou(e.obs)}</td></tr>`,
    )
    .join("")}</tbody></table>`;
}

function tabelaInspecao(itens: ItemInspecao[]): string {
  const linhas = naOrdem(itens, INSPECAO_FINAL, "item", (n) => ({ item: n, resposta: "", obs: "" }));
  return `<table><thead><tr><th>Item</th><th class="res">Resposta</th><th>Observação</th></tr></thead><tbody>${linhas
    .map(
      (i) =>
        `<tr><td>${esc(i.item)}</td><td class="res ${tom(i.resposta)}">${ou(i.resposta)}</td><td>${ou(i.obs)}</td></tr>`,
    )
    .join("")}</tbody></table>`;
}

/** Os eventos que mudam a etapa do trafo (entrada, avanço, devolução, conclusão). */
const MUDA_ETAPA = ["entrada", "etapa", "devolucao", "conclusao"];

/**
 * A passagem pelas etapas, com o tempo em cada uma — sai do próprio histórico:
 * cada evento que muda a etapa abre um período, que vai até o próximo.
 */
function passagens(eventos: EventoTrafo[], emitidoEm: string): string {
  const marcos = eventos.filter((e) => MUDA_ETAPA.includes(e.tipo));
  if (marcos.length === 0) return `<p class="nota">Sem registro de passagem pelas etapas.</p>`;
  const fim = Date.parse(emitidoEm);
  return `<table><thead><tr><th class="quando">Quando</th><th>Etapa</th><th>Movimento</th><th>Por</th><th class="tempo">Tempo na etapa</th></tr></thead><tbody>${marcos
    .map((e, i) => {
      const prox = marcos[i + 1];
      const inicio = Date.parse(e.created_at);
      const tempo =
        e.etapa === CONCLUIDO
          ? "—"
          : prox
            ? duracaoTexto(Date.parse(prox.created_at) - inicio)
            : `${duracaoTexto(fim - inicio)} (em andamento)`;
      const etapa = e.etapa === CONCLUIDO ? "Concluído" : `${infoEtapa(e.etapa)?.numero ?? ""}. ${nomeEtapa(e.etapa)}`;
      const classe = e.tipo === "devolucao" ? ' class="devol"' : e.tipo === "conclusao" ? ' class="concl"' : "";
      return `<tr${classe}><td class="quando">${esc(dataHoraLonga(e.created_at))}</td><td>${esc(etapa)}</td><td>${esc(
        e.texto,
      )}</td><td>${ou(e.usuario)}</td><td class="tempo">${esc(tempo)}</td></tr>`;
    })
    .join("")}</tbody></table>`;
}

function historico(eventos: EventoTrafo[]): string {
  if (eventos.length === 0) return `<p class="nota">Nenhum registro.</p>`;
  return `<table class="hist"><thead><tr><th class="quando">Data e hora</th><th>Etapa</th><th>Registro</th><th>Por</th></tr></thead><tbody>${eventos
    .map(
      (e) =>
        `<tr${MUDA_ETAPA.includes(e.tipo) ? ' class="marco"' : ""}><td class="quando">${esc(
          dataHoraLonga(e.created_at),
        )}</td><td>${esc(nomeEtapa(e.etapa))}</td><td>${esc(e.texto)}</td><td>${ou(e.usuario)}</td></tr>`,
    )
    .join("")}</tbody></table>`;
}

function anexoFotos(t: RastreabilidadeTrafo): string {
  if (t.fotos.length === 0) return "";
  const grupos = ETAPAS.map((etapa) => ({ etapa, fotos: t.fotos.filter((f) => f.etapa === etapa) })).filter(
    (g) => g.fotos.length > 0,
  );
  return `<section class="anexo">
    <h2>Anexo — fotos (${t.fotos.length})</h2>
    ${grupos
      .map(
        (g) => `<h3>${infoEtapa(g.etapa)?.numero}. ${esc(infoEtapa(g.etapa)?.titulo ?? g.etapa)}</h3>
      <div class="fotos">${g.fotos
        .map(
          (f) => `<figure><img src="${esc(f.url)}" alt="Foto ${f.id}"><figcaption>${
            f.legenda ? `${esc(f.legenda)}<br>` : ""
          }<span>${ou(f.usuario)} · ${esc(dataHoraLonga(f.created_at))}</span></figcaption></figure>`,
        )
        .join("")}</div>`,
      )
      .join("")}
  </section>`;
}

/* ------------------------------------------------------------------ folha --- */

export function montarFolhaTrafo(t: RastreabilidadeTrafo): string {
  const concluido = t.etapa === CONCLUIDO;
  const placa = t.placa ?? {};
  const titulo = `Rastreabilidade do trafo nº ${t.id} — ${t.cliente}`;
  const descricao = descricaoTrafo({
    fabricante: placa.fabricante as string,
    potencia_kva: placa.potencia_kva as string,
    numero_serie: placa.numero_serie as string,
    identificacao: t.identificacao,
  });
  const sub = [descricao, t.os_numero ? `OS ${t.os_numero}` : "", t.nota_fiscal ? `NF ${t.nota_fiscal}` : ""]
    .filter(Boolean)
    .map(esc)
    .join(" · ");

  // Situação + tempo de oficina (da data de entrada até a conclusão, ou até hoje).
  const ateQuando = concluido && t.data_conclusao ? ymdLocal(t.data_conclusao) : ymdLocal(t.emitido_em);
  const dias = t.data_entrada ? diasEntre(t.data_entrada, ateQuando) : null;
  const tempoOficina =
    dias == null
      ? ""
      : `${dias} dia${dias === 1 ? "" : "s"} na oficina (entrada ${dataBR(t.data_entrada)} → ${
          concluido ? `conclusão ${dataBR(ateQuando)}` : `hoje, ${dataBR(ateQuando)}`
        })`;
  const situacao = concluido
    ? `<div class="situacao fim"><strong>Concluído e liberado</strong> — aprovado pela Qualidade em ${esc(
        dataHoraLonga(t.data_conclusao),
      )}${t.responsavel_qualidade ? ` por ${esc(t.responsavel_qualidade)}` : ""}.<br><span>${esc(tempoOficina)}</span></div>`
    : `<div class="situacao andamento"><strong>Em andamento</strong> — etapa atual: ${esc(
        `${infoEtapa(t.etapa)?.numero ?? ""}. ${nomeEtapa(t.etapa)}`,
      )}, desde ${esc(dataHoraLonga(t.etapa_desde))}.${
        t.previsao_entrega ? ` Entrega prevista: ${esc(dataBR(t.previsao_entrega))}.` : ""
      }<br><span>${esc(tempoOficina)}</span></div>`;

  // 1. Entrada
  const entrada = tabelaKV([
    ["Cliente", ou(t.cliente)],
    ["OS", ou(t.os_numero)],
    ["Nota fiscal", ou(t.nota_fiscal)],
    ["Identificação", ou(t.identificacao)],
    ["Data de entrada", esc(dataBR(t.data_entrada))],
    ["Previsão de entrega", esc(dataBR(t.previsao_entrega))],
    ["Registrado por", `${ou(t.criado_por)} em ${esc(dataHoraLonga(t.created_at))}`],
  ]);
  const chegada = vazio(t.condicao_chegada)
    ? ""
    : `<p class="rotulo">Condição de chegada</p><div class="txt">${esc(t.condicao_chegada)}</div>`;

  // 2. Placa
  const camposPlaca = CAMPOS_PLACA.filter((c) => !vazio(placa[c.key]));
  const blocoPlaca =
    placa.sem_placa === true && camposPlaca.length === 0
      ? `<p class="nota">Placa ausente ou ilegível.</p>`
      : camposPlaca.length === 0
        ? `<p class="nota">Dados da placa ainda não coletados.</p>`
        : `${placa.sem_placa === true ? `<p class="nota">Marcado como placa ausente ou ilegível.</p>` : ""}<table class="kv duas">${pares(
            camposPlaca.map((c) => [c.label, esc(placa[c.key])] as [string, string]),
          )}</table>`;

  // Serviços (definidos na etapa 2, andamento na 4)
  const servicos =
    t.servicos.length === 0
      ? `<p class="nota">Nenhum serviço definido.</p>`
      : `<table><thead><tr><th>Serviço</th><th class="res">Status</th><th>Observação</th><th>Último registro</th></tr></thead><tbody>${t.servicos
          .map(
            (s) =>
              `<tr><td>${esc(s.nome)}</td><td class="res ${
                s.status === "Concluído" ? "ok" : s.status === "Cancelado" ? "neutro" : ""
              }">${esc(s.status)}</td><td>${ou(s.obs)}</td><td>${ou(s.atualizado_por)}${
                s.atualizado_em ? `<br><span class="nota">${esc(dataHoraLonga(s.atualizado_em))}</span>` : ""
              }</td></tr>`,
          )
          .join("")}</tbody></table>`;

  // 3. Coleta de óleo
  const c = t.coleta_oleo ?? {};
  const rotuloAnalise = (k: string) => ANALISES_OLEO.find((a) => a.key === k)?.label ?? k;
  const coleta =
    vazio(c.aplica)
      ? `<p class="nota">Coleta de óleo não informada.</p>`
      : c.aplica !== "Sim"
        ? `<p>Coleta de óleo: <strong>${esc(c.aplica)}</strong>${c.obs ? ` — ${esc(c.obs)}` : ""}</p>`
        : tabelaKV([
            ["Coleta de óleo", "Sim"],
            ["Data da coleta", esc(dataBR(c.data ?? null))],
            ["Análises pedidas", (c.analises ?? []).map((a) => esc(rotuloAnalise(a))).join(", ") || "—"],
            ["Amostra", ou(c.amostra)],
            ["Temperatura", ou(c.temperatura)],
            ["Observação", ou(c.obs)],
          ]);

  // 5. Qualidade
  const parecer = concluido ? "Aprovado" : t.parecer_final ?? "";
  const qualidade = tabelaKV([
    ["Parecer final", parecer ? `<strong class="${tom(parecer)}">${esc(parecer)}</strong>` : "— (ainda não avaliado)"],
    ["Responsável da Qualidade", ou(t.responsavel_qualidade)],
    ["Data da aprovação", esc(dataHoraLonga(t.data_conclusao))],
  ]);
  const obsQualidade = vazio(t.parecer_obs)
    ? ""
    : `<p class="rotulo">Observações da Qualidade</p><div class="txt">${esc(t.parecer_obs)}</div>`;

  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
    <meta name="viewport" content="width=device-width,initial-scale=1">
    <title>${esc(titulo)}</title>
    <style>
      *{box-sizing:border-box}
      body{font-family:system-ui,Segoe UI,sans-serif;color:#13241b;margin:24px;font-size:12px;line-height:1.45}
      /* Faixa verde da SERCAMP em CSS puro (imagem externa pode não carregar a
         tempo na janela de impressão); print-color-adjust obriga a imprimir o fundo. */
      .cab{position:relative;overflow:hidden;border-radius:6px;color:#fff;min-height:70px;
        padding:14px 20px 14px 128px;display:flex;flex-direction:column;justify-content:center;
        background:linear-gradient(135deg,#0f4b2c 0%,#14472e 60%,#0c7a3f 100%);
        -webkit-print-color-adjust:exact;print-color-adjust:exact}
      .cab-listras{position:absolute;left:0;top:0;bottom:0;width:108px;
        background:repeating-linear-gradient(135deg,rgba(255,255,255,.30) 0 6px,rgba(255,255,255,0) 6px 15px);
        -webkit-print-color-adjust:exact;print-color-adjust:exact}
      .cab-nome{font-size:22px;font-weight:800;letter-spacing:.02em;line-height:1.15}
      .cab-slogan{font-size:11px;letter-spacing:.06em;text-transform:uppercase;opacity:.92;margin-top:3px}
      h1{font-size:16px;margin:18px 0 2px;text-align:center}
      .sub{text-align:center;color:#5e7268;font-size:11.5px;margin:0 0 12px}
      h2{font-size:12px;margin:18px 0 6px;color:#0c7a3f;text-transform:uppercase;letter-spacing:.05em;
        border-bottom:1px solid #cbd8d0;padding-bottom:3px}
      h3{font-size:12px;margin:12px 0 6px}
      p{margin:0 0 6px}
      .nota{color:#5e7268;font-size:11px}
      .rotulo{font-size:10.5px;color:#5e7268;text-transform:uppercase;letter-spacing:.04em;margin:8px 0 3px}
      .txt{white-space:pre-wrap;border:1px solid #cbd8d0;border-radius:6px;padding:7px 10px}
      .situacao{border:1px solid #cbd8d0;border-left:4px solid #0c7a3f;border-radius:6px;padding:8px 12px;
        background:#f4f9f6;font-size:12.5px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .situacao.andamento{border-left-color:#e9a13b;background:#fdf7e8}
      .situacao span{font-size:11px;color:#5e7268}
      table{width:100%;border-collapse:collapse;font-size:11px;margin:0 0 6px}
      th,td{border:1px solid #cbd8d0;padding:4px 6px;text-align:left;vertical-align:top}
      /* Texto longo sem espaço (e-mail) quebra; status e cabeçalho ficam inteiros. */
      td{overflow-wrap:anywhere}
      td.res{overflow-wrap:normal}
      thead th{background:#eaf5ee;font-size:10px;text-transform:uppercase;letter-spacing:.04em;
        -webkit-print-color-adjust:exact;print-color-adjust:exact}
      .kv th{width:30%;background:#f4f9f6;font-weight:600;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .kv.duas th{width:20%}
      .res{width:92px;white-space:nowrap}
      .ok{color:#0c7a3f;font-weight:700}
      .nok{color:#b42318;font-weight:700}
      .neutro{color:#5e7268}
      .quando{width:118px;white-space:nowrap}
      .tempo{width:110px}
      tr.devol td{background:#fdf2f0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      tr.concl td{background:#eef8f2;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .hist tr.marco td{font-weight:600}
      .ass{margin-top:40px;display:flex;justify-content:space-between;gap:40px}
      .ass div{flex:1;border-top:1px solid #333;padding-top:5px;font-size:11px;text-align:center}
      .emissao{font-size:10.5px;color:#5e7268;border-top:1px solid #cbd8d0;padding-top:6px;margin-top:16px}
      .fotos{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
      figure{margin:0;border:1px solid #cbd8d0;border-radius:6px;padding:5px;break-inside:avoid;page-break-inside:avoid}
      figure img{width:100%;height:170px;object-fit:contain;background:#f4f6f5;display:block}
      figcaption{font-size:10px;margin-top:4px}
      figcaption span{color:#5e7268}
      /* Barra só da tela: imprimir de novo e tirar as fotos (não sai no papel). */
      .barra{position:sticky;top:0;display:flex;gap:8px;align-items:center;justify-content:flex-end;
        background:#fff;padding:8px 0;margin:-8px 0 8px;border-bottom:1px solid #cbd8d0;z-index:2}
      .barra button{font:inherit;font-size:12.5px;padding:7px 14px;border-radius:7px;cursor:pointer;
        border:1px solid #0c7a3f;background:#0c7a3f;color:#fff}
      .barra button.sec{background:#fff;color:#0c7a3f}
      .barra span{margin-right:auto;color:#5e7268;font-size:11.5px}
      body.sem-fotos .anexo{display:none}
      /* Celular (o app do colaborador abre o relatório no telefone): nada pode
         ficar mais largo que a tela — datas e status quebram linha. */
      @media (max-width:640px){
        body{margin:12px}
        .cab{padding-left:20px}.cab-listras{display:none}
        .fotos{grid-template-columns:repeat(2,1fr)}
        .kv th{width:40%}.kv.duas th{width:auto}
        .quando,.res,.tempo{white-space:normal;width:auto}
        .barra{flex-wrap:wrap}.barra span{flex:1 1 100%}
      }
      @media print{
        body{margin:10mm}
        .barra{display:none}
        h2,h3{page-break-after:avoid}
        .cab,.situacao,.ass,tr{page-break-inside:avoid}
        thead{display:table-header-group}
        .anexo{page-break-before:always}
      }
    </style></head><body>

    <div class="barra">
      <span>Para PDF: escolha "Salvar como PDF" no destino da impressão.</span>
      ${
        t.fotos.length > 0
          ? `<button type="button" class="sec" onclick="document.body.classList.toggle('sem-fotos');this.textContent=document.body.classList.contains('sem-fotos')?'Incluir as fotos':'Tirar as fotos'">Tirar as fotos</button>`
          : ""
      }
      <button type="button" onclick="window.print()">Imprimir / Salvar PDF</button>
    </div>

    <header class="cab">
      <div class="cab-listras"></div>
      <div class="cab-nome">Sercamp Serviços</div>
      <div class="cab-slogan">Completa em Manutenção e Prevenção Elétrica</div>
    </header>

    <h1>${esc(titulo)}</h1>
    <p class="sub">${sub}</p>
    ${situacao}

    <h2>1. Entrada do trafo</h2>
    ${entrada}
    ${chegada}

    <h2>2. Placa de identificação</h2>
    ${blocoPlaca}

    <h2>Serviços executados</h2>
    ${servicos}

    <h2>3. Ensaios elétricos de entrada</h2>
    ${tabelaEnsaios(t.ensaios_entrada, ENSAIOS_ENTRADA)}
    <h3>Coleta de óleo</h3>
    ${coleta}

    <h2>5. Ensaios finais e inspeção (Qualidade)</h2>
    ${tabelaEnsaios(t.ensaios_finais, ENSAIOS_FINAIS)}
    <h3>Inspeção final</h3>
    ${tabelaInspecao(t.inspecao_final)}
    ${qualidade}
    ${obsQualidade}

    <h2>Passagem pelas etapas</h2>
    ${passagens(t.eventos, t.emitido_em)}

    <h2>Histórico completo (${t.eventos.length} registro${t.eventos.length === 1 ? "" : "s"})</h2>
    ${historico(t.eventos)}

    <div class="ass"><div>Responsável pela Qualidade</div><div>Gestão da Operação</div></div>

    <p class="emissao">Emitido em ${esc(dataHoraLonga(t.emitido_em))} por ${esc(t.emitido_por)}. Dados e histórico
    registrados automaticamente pelo sistema a cada ação da equipe (quem fez, o quê e quando).</p>

    ${anexoFotos(t)}
    </body></html>`;
}

/** Tabela de 4 colunas (rótulo, valor, rótulo, valor) — a placa cabe em meia página. */
function pares(linhas: [string, string][]): string {
  const out: string[] = [];
  for (let i = 0; i < linhas.length; i += 2) {
    const [k1, v1] = linhas[i]!;
    const par = linhas[i + 1];
    out.push(
      `<tr><th>${esc(k1)}</th><td>${v1}</td>${par ? `<th>${esc(par[0])}</th><td>${par[1]}</td>` : "<th></th><td></td>"}</tr>`,
    );
  }
  return out.join("");
}

/* ------------------------------------------------------------- imprimir --- */

/** Espera as fotos carregarem (senão a impressão sai com os quadros vazios). */
async function imagensProntas(janela: Window, limiteMs = 15_000): Promise<void> {
  const imgs = Array.from(janela.document.images);
  const todas = Promise.all(
    imgs.map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>((ok) => {
            img.addEventListener("load", () => ok(), { once: true });
            img.addEventListener("error", () => ok(), { once: true });
          }),
    ),
  );
  await Promise.race([todas, new Promise<void>((ok) => setTimeout(ok, limiteMs))]);
}

/**
 * Abre o relatório numa janela nova e chama a impressão (de onde sai o PDF).
 *
 * A janela abre AGORA, dentro do clique — aberta depois da espera, o navegador
 * a trataria como pop-up não pedido e bloquearia. Devolve `null` quando deu
 * certo, ou a mensagem para mostrar na tela.
 */
export function imprimirRastreabilidade(id: number): Promise<string | null> {
  const janela = window.open("", "_blank");
  if (!janela) {
    return Promise.resolve("O navegador bloqueou a janela do relatório. Libere os pop-ups deste site e tente de novo.");
  }
  try {
    janela.document.write(
      '<p style="font-family:system-ui,sans-serif;padding:24px;color:#13241b">Montando o relatório de rastreabilidade…</p>',
    );
  } catch {
    /* só o aviso de espera que não aparece */
  }
  return (async () => {
    try {
      const dados = await apiTrafos.rastreabilidade(id);
      janela.document.open();
      janela.document.write(montarFolhaTrafo(dados));
      janela.document.close();
      await imagensProntas(janela);
      janela.focus();
      janela.print();
      return null;
    } catch (err) {
      janela.close();
      return err instanceof ApiError ? err.message : "Não foi possível montar o relatório. Tente de novo.";
    }
  })();
}
